import React from "react"
import {
  CustomState,
  OrderSearchHit,
  OrderSearchHits,
  StoreGroup
} from "lib/types/generated/graphql-types"
import DineroFactory from "dinero.js"
import dayjs from "dayjs"
import {
  getPaymentMethodDisplayName,
  getPaymentProviderDisplayName
} from "helpers/getPaymentDisplayName"
import { getProviderIcon } from "helpers/providers"
import { useQuery } from "@apollo/client"
import { ReactComponent as InfoIcon } from "../../../images/icons/circle-info.svg"

import ALL_STORE_GROUPS from "../../../graphql/queries/store/AllStoreGroups"

import {
  Container,
  Customer,
  EmptyList,
  FlexColumn,
  IconContainer,
  OrderDate,
  PaymentContainer,
  PaymentMethod,
  SmallInfo,
  StatusColumn,
  StatusHeader,
  StoreGroupColumn,
  StyledTableHeader
} from "./OrderTable.styled"

import TableRow from "../../Ui/Table/TableRow"
import Money from "../../Money"
import FlagIcon from "../../Ui/FlagIcon"
import TableBlurLoading from "../../Ui/Table/TableBlurLoading"
import ErrorMessage from "../../Ui/Messages/ErrorMessage"
import CopyButton from "../../Ui/CopyButton"
import DotLoader from "../../Ui/DotLoader"

import { ReactComponent as CartIcon } from "../../../images/icons/cart-shopping.svg"
import { Tooltip } from "../../Ui/Tooltip"
import TableStatus from "./TableStatus"
import { selectSidebarCollapsed } from "lib/store/services/ui/selectors"
import { useSelector } from "react-redux"

type Props = {
  orderData: OrderSearchHits | undefined
  loading: boolean
  searchQuery: string
  error: object | undefined
  customStates: CustomState[]
}

export const OrderTable = ({ orderData, loading, searchQuery, error, customStates }: Props) => {
  const sideBarCollapsed = useSelector(selectSidebarCollapsed)
  const { data, loading: storeGroupLoading } = useQuery(ALL_STORE_GROUPS)
  const { getStoreGroups: storeGroups } = data ?? []

  const tableHeader = (
    <StyledTableHeader>
      <div>Reference</div>
      <Customer className="hideOnSmallScreens">Customer</Customer>
      <div>Total order value</div>
      {sideBarCollapsed && (
        <PaymentContainer className="hideOnSmallScreens hideOnMediumScreens">
          Payment
        </PaymentContainer>
      )}
      <StoreGroupColumn className="hideOnSmallScreens hideOnMediumScreens">
        Store group
      </StoreGroupColumn>
      <FlexColumn className={sideBarCollapsed ? "hideOnSmallScreens" : "hideOnMediumScreens"}>Order date</FlexColumn>
      <StatusHeader className="hideOnSmallScreens hideOnMediumScreens">Status</StatusHeader>
    </StyledTableHeader>
  )

  const getRows = (order?: OrderSearchHit) =>
    order ? (
      <TableRow
        key={order.id}
        to={`/orders/${order.id}`}
      >
        <FlagIcon countryCode={order.countryCode} />
        <FlexColumn>
          <div>
            {order.reference} <CopyButton string={order.reference} />
            {order.merchantReference1 && (
              <SmallInfo
                className={"hideOnSmallScreens"}
                data-tooltip-id="tooltip"
                data-tooltip-content={order.merchantReference1}
                data-tooltip-delay-hide={100}
              >
                Merchant ref. 1
                <InfoIcon />
              </SmallInfo>
            )}
            {order.merchantReference2 && (
              <SmallInfo
                className={"hideOnSmallScreens"}
                data-tooltip-id="tooltip"
                data-tooltip-content={order.merchantReference2}
                data-tooltip-delay-hide={100}
              >
                Merchant ref. 2
                <InfoIcon />
              </SmallInfo>
            )}
          </div>
        </FlexColumn>
        <Customer className="hideOnSmallScreens">
          <div>
            {order.billingAddress.givenName} {order.billingAddress.familyName}
            <SmallInfo>{order.billingAddress?.email}</SmallInfo>
          </div>
        </Customer>
        <FlexColumn>
          <p>
            <Money
              amount={parseInt(`${order.totals?.grandTotal}`)}
              currencyUnit={order.currencyCode as DineroFactory.Currency}
            />
          </p>
        </FlexColumn>
        {sideBarCollapsed && (
          <PaymentContainer
            className="hideOnSmallScreens hideOnMediumScreens"
          >
            <IconContainer>{getProviderIcon(order.paymentProviderName)}</IconContainer>
            <span>
              <SmallInfo>{getPaymentProviderDisplayName(order.paymentProviderName)}</SmallInfo>
              <PaymentMethod>{getPaymentMethodDisplayName(order.paymentMethod)}</PaymentMethod>
            </span>
          </PaymentContainer>
        )}
        <StoreGroupColumn className="hideOnSmallScreens hideOnMediumScreens">
          {storeGroupLoading ? (
            <DotLoader />
          ) : (
            <SmallInfo>
              {storeGroups?.find((sg: StoreGroup) => sg.id === order.storeGroupId)?.name}
            </SmallInfo>
          )}
        </StoreGroupColumn>
        <OrderDate className={sideBarCollapsed ? "hideOnSmallScreens" : "hideOnMediumScreens"}>
          <div>{dayjs(order.date).format("YYYY-MM-DD")}</div>
          <SmallInfo>{dayjs(order.date).format("HH:mm")}</SmallInfo>
        </OrderDate>
        <TableStatus order={order} customStates={customStates} />
      </TableRow>
    ) : (
      <>
        <FlagIcon countryCode="dummy" />
        <FlexColumn>
          <p>777455dhd6c</p>
        </FlexColumn>
        <Customer className="hideOnSmallScreens">
          <div>
            John Doe
            <SmallInfo>john.doe@email.com</SmallInfo>
          </div>
        </Customer>
        <FlexColumn>
          <p>SEK 100</p>
        </FlexColumn>
        <PaymentContainer className="hideOnSmallScreens hideOnMediumScreens">
          Klarna
          <PaymentMethod>Card</PaymentMethod>
        </PaymentContainer>
        <StoreGroupColumn className="hideOnSmallScreens hideOnMediumScreens">
          Brink Commerce
        </StoreGroupColumn>
        <OrderDate className="hideOnSmallScreens">
          <p>2000-00-00 00:00</p>
        </OrderDate>
        <StatusColumn className="hideOnSmallScreens hideOnMediumScreens"></StatusColumn>
      </>
    )

  if (loading && !searchQuery)
    return <TableBlurLoading numberOfRows={4} tableHeader={tableHeader} rows={getRows()} />

  return (
    <Container>
      <Tooltip id="tooltip" />
      {error && (
        <ErrorMessage
          showRefreshButton
          message={
            <>
              Error loading Orders. Try refreshing the page, or contact{" "}
              <a href="mailto:support@brinkcommerce.com">support</a>.
            </>
          }
        />
      )}
      {orderData && orderData?.hits?.length > 0 ? (
        <>
          {tableHeader}
          <div>{orderData.hits.map((order) => getRows(order))}</div>
        </>
      ) : (
        <EmptyList>
          <CartIcon />
          <p> No orders found</p>
        </EmptyList>
      )}
    </Container>
  )
}
